import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AccountType } from '@leedpulse-monorepo/enums';
import {
  StepPersonalInfo,
  StepCompany,
  StepActivity,
  StepPlan,
  StepActivationCode,
} from '../../components/sign_up_page/steps';
import NumbersContext from '../numbers/NumbersContext';
import PlansContext from '../plans/PlansContext';
import { BillingDetails } from '@stripe/stripe-js';
import createUser from '../../../domain/use_cases/users/createUser';
import confirmAccount from '../../../domain/use_cases/users/confirmAccount';
import { ICompany } from '@leedpulse-monorepo/interfaces';
import { ISubscription } from '@leedpulse-monorepo/interfaces';
import HttpClient from '../../../core/HttpClient';
import { message } from 'antd';

const BUSINESS_DOMAINS = [{ label: 'Immobilier', value: 'domain-8F28730C7D' }];
const VALID_ACTIVATION_CODE = 'LEEDFLOW2026'; // TODO : @MrWarzo fetch sur la conf back

interface StepItem {
  content: () => React.ReactNode;
  validate: () => boolean;
}

// TODO : @MrWarzo rendre ça plus strong pour gerer le fait que des etapes peuvent disparaitres
export enum StepIndex {
  PERSONAL_INFO = 0,
  COMPANY = 1,
  ACTIVITY = 2,
  PLAN = 3,
  ACTIVATION_CODE = 4,
}

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  companyName: string;
  companySiret: string;
  businessDomain: string;
  activityDescription: string;
  // Company & user object created from here
  plan: string;
  professionalNumber: string;
  activationCode: string;
  accountType: AccountType;
  paymentData: {
    cardOwner: string;
    billingDetails: BillingDetails;
  };
};

interface SignupFormContextValue {
  data: {
    formData: FormData;
    steps: StepItem[];
    submitError: string | null;
    currentStep: StepIndex;
    canProceed: boolean;
    submitButtonTarget: React.RefObject<HTMLDivElement> | null;
    accountCreated: { company: ICompany, userUid: string, subscription?: ISubscription } | null;
    paymentSuccessful: boolean;
    isValidActivationCode: boolean;
    signupJWT: string | null;
    isLoading: boolean;
  }
  actions: {
    setCurrentStep: React.Dispatch<React.SetStateAction<StepIndex>>;
    setSubmitError: (_: string | null) => void;
    updateFormData: (_: keyof FormData, __: string | FormData[keyof FormData]) => void;
    handleCreateCompanyAndUser: () => void; // Pre-create company + user as incomplete
    handleConfirmAccount: () => void; // Confirm account: remove incomplete flag + send welcome email
    handleSubmitAccountCreation: () => void; // Confirm company creation (payment flow)
  }
}

const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  companyName: '',
  companySiret: '',
  businessDomain: BUSINESS_DOMAINS[0].value,
  activityDescription: '',
  plan: '',
  professionalNumber: '',
  activationCode: '',
  accountType: AccountType.COMPANY,
  paymentData: {
    cardOwner: '',
    billingDetails: {
      name: '',
      address: {
        line1: '',
        line2: null,
        city: '',
        state: '',
        postal_code: '',
        country: '',
      },
    },
  },
};

// Mock data dev only
// const initialFormData: FormData = {
//   firstName: 'TEST',
//   lastName: 'TEST',
//   phone: '699887766',
//   email: 'test@test.com',
//   companyName: 'TEST',
//   companySiret: '379 264 435 17506',
//   businessDomain: 'domain-8F28730C7D',
//   activityDescription: 'TEST',
//   plan: 'lf-monthly-premium',
//   professionalNumber: '1234567890',
//   activationCode: 'LEEDFLOW2026',
//   accountType: AccountType.SUBSCRIPTION,
//   paymentData: {
//     cardOwner: 'TEST',
//     billingDetails: {
//       name: 'TEST',
//       address: {
//         line1: '123 Rue de la Paix',
//         line2: 'Appartement 1',
//         city: 'Paris',
//         state: 'Île-de-France',
//         postal_code: '75000',
//         country: 'FR',
//       },
//     },
//   },
// };


const SignupFormContext = createContext<SignupFormContextValue>({
  data: {
    formData: initialFormData,
    steps: [],
    submitError: null,
    currentStep: StepIndex.PERSONAL_INFO,
    canProceed: false,
    submitButtonTarget: null,
    accountCreated: null,
    paymentSuccessful: false,
    isValidActivationCode: false,
    signupJWT: null,
    isLoading: false,
  },
  actions: {
    setCurrentStep: () => void 0,
    setSubmitError: (_: string | null) => void 0,
    updateFormData: (_: keyof FormData, __: string | FormData[keyof FormData]) => void 0,
    handleCreateCompanyAndUser: () => void 0,
    handleConfirmAccount: () => void 0,
    handleSubmitAccountCreation: () => void 0,
  },
});

export const SignupFormProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  /**
   * Contexts
   */
  const numbersContext = useContext(NumbersContext);
  const { plans, fetchPlans } = useContext(PlansContext);

  /**
   * Refs
   */
  const submitButtonTarget = useRef<HTMLDivElement>(null);

  /**
   * States
   */
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(StepIndex.PERSONAL_INFO);
  const [accountCreated, setAccountCreated] = useState<{ company: ICompany, userUid: string, subscription?: ISubscription } | null>(null);
  const [signupJWT, setSignupJWT] = React.useState<string | null>(null);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Memos / consts
   */
  const isValidActivationCode = useMemo(() => {
    const isValid = formData.activationCode.toUpperCase() === VALID_ACTIVATION_CODE;

    // Will refetch a link for every change in the form (isnt optimal but we need to keep the link updated)
    if (isValid && currentStep === StepIndex.ACTIVATION_CODE) {
      if (!accountCreated) {
        message.error('Le compte n\'a pas été pré-créé, une erreur est survenue.');
        return false;
      }

      HttpClient.post('/leedflow/signup/generate-jwt', {
        accountCreated: {
          userUid: accountCreated.userUid,
          company: accountCreated.company,
        },
        formData,
      }, "").then((result) => {
        setSignupJWT(result.data.token);
        setSubmitError(null);
      }).catch((error) => {
        setSubmitError(error instanceof Error ? error.message : 'Une erreur est survenue');
      });
    }

    return !!isValid;
  }, [formData, accountCreated]);

  const selectedDomainLabel =
    BUSINESS_DOMAINS.find((domain) => domain.value === formData.businessDomain)?.label ||
    'Sélectionnez un domaine';

  /**
   * Functions
   */
  // Validation du format de numéro de téléphone international
  const isValidPhoneNumber = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[^0-9+]/g, '');

    // Formats internationaux acceptés (indicatif + numéro local)
    // France: +33 suivi de 9 chiffres (ex: +33612345678)
    // Allemagne: +49 suivi de 10-11 chiffres (ex: +491512345678)
    // Suisse: +41 suivi de 9 chiffres (ex: +41791234567)
    // UK: +44 suivi de 10 chiffres (ex: +447912345678)
    const internationalRegex = /^\+(33[0-9]{9}|49[0-9]{10,11}|41[0-9]{9}|44[0-9]{10})$/;

    return internationalRegex.test(cleanPhone);
  };

  // Validation stricte du format email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const updateFormData = (field: keyof FormData, value: string | FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSubmitError(null);
  };

  const getPlans = async () => {
    await fetchPlans();
  };

  const handleCreateCompanyAndUser = async () => {
    // Skip if already created (idempotent)
    if (accountCreated) {
      return;
    }

    setIsLoading(true);
    setSubmitError(null);

    try {
      // Pre-create user + company as incomplete (no welcome email yet)
      const response = await createUser({
        email: formData.email,
        firstname: formData.firstName,
        lastname: formData.lastName,
        phone: formData.phone,
        name: formData.companyName,
        siret: formData.companySiret,
        domain: formData.businessDomain,
        intro: formData.activityDescription || '',
        id_plan: formData.plan,
        sender: formData.professionalNumber,
        activation_code: formData.activationCode,
        type: AccountType.COMPANY,
      });

      const result = await response;

      if (result.success && result.data && result.data.company && result.data.uid) {
        setAccountCreated({
          userUid: result.data.uid,
          company: result.data.company,
        });
      } else {
        throw new Error(result.error || 'Une erreur est survenue');
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Une erreur est survenue lors de la création du compte'
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Confirm the account: remove `incomplete` flag from user & company,
   * and trigger the welcome email.
   */
  const handleConfirmAccount = async () => {
    if (!accountCreated) {
      setSubmitError('Le compte n\'a pas encore été créé.');
      return;
    }

    // Validate activation code before confirming
    if (!isValidActivationCode) {
      setSubmitError("Code d'activation invalide. Veuillez vérifier et réessayer.");
      return;
    }

    setIsLoading(true);
    setSubmitError(null);

    try {
      await confirmAccount({
        userUid: accountCreated.userUid,
        companyId: accountCreated.company.id,
      });

      setPaymentSuccessful(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Une erreur est survenue lors de la confirmation du compte'
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Steps (not with other memo bc it have a lot of dependency functions)
   */
  const steps: StepItem[] = useMemo(() => [
    {
      // title: 'Informations personnelles',
      content: () => (
        <StepPersonalInfo
          onFirstNameChange={(value) => updateFormData('firstName', value)}
          onLastNameChange={(value) => updateFormData('lastName', value)}
          onEmailChange={(value) => updateFormData('email', value)}
          onPhoneChange={(value) => updateFormData('phone', value)}
          stepTitle={'Informations personnelles'}
        />),
      validate: () => (
        (formData.firstName.trim() !== '' && formData.lastName.trim() !== '') &&
        (formData.phone.trim() !== '' && isValidPhoneNumber(formData.phone)) &&
        (isValidEmail(formData.email))
      ),
    },
    {
      // title: 'Société',
      content: () => (
        <StepCompany
          onCompanyNameChange={(value) => updateFormData('companyName', value)}
          onCompanySiretChange={(value) => updateFormData('companySiret', value)}
          stepTitle={'Société'}
        />),
      validate: () => formData.companyName.trim().length >= 3 && formData.companySiret.trim().length >= 1,
    },
    {
      content: () => (
        <StepActivity
          businessDomain={formData.businessDomain}
          activityDescription={formData.activityDescription}
          onBusinessDomainChange={(value) => updateFormData('businessDomain', value)}
          onActivityDescriptionChange={(value) => updateFormData('activityDescription', value)}
          stepTitle={'Activité'}
          businessDomains={BUSINESS_DOMAINS}
        />),
      validate: () => formData.businessDomain !== '',
    },
    {
      content: () => (
        <StepPlan
          selectedPlan={formData.plan}
          onPlanChange={(value) => updateFormData('plan', value)}
          onAccountTypeChange={(value) => {
            updateFormData('accountType', value);
            updateFormData('plan', '');
          }}
          stepTitle={'Formule d\'abonnement'}
          plans={plans}
          accountType={formData.accountType}
        />),
      validate: () => (formData.accountType === AccountType.SUBSCRIPTION && formData.plan !== '') || (formData.accountType === AccountType.COMPANY),
    },
    {
      content: () => {
        const selectedNumberData = numbersContext.items.find(
          (n) => n.id === formData.professionalNumber
        );
        return (
          <StepActivationCode
            onActivationCodeChange={(value) => updateFormData('activationCode', value)}
            isValidActivationCode={isValidActivationCode}
            stepTitle={"Nouveau compte"}
            submitError={submitError}
            selectedPlanId={formData.plan}
            selectedPlanData={plans.find((p) => p.id === formData.plan) || undefined}
            businessDomainLabel={selectedDomainLabel}
            professionalNumber={selectedNumberData?.formattedNumber || ''}
          />);
      },
      validate: () => formData.activationCode.trim() !== '',
    },
  ].filter(Boolean) as StepItem[], [
    formData,
    submitError,
    plans,
    numbersContext.items,
    selectedDomainLabel,
    isValidActivationCode,
  ]);

  const canProceed = useMemo(() => {
    return steps[currentStep]?.validate();
  }, [steps, currentStep]);

  const handleSubmitAccountCreation = async () => {
    if (canProceed) {
      // Confirm the incomplete account (remove flag + send welcome email)
      await handleConfirmAccount();
    }
  };

  const verifySignupJWT = async (jwtToken: string) => {
    try {
      const response = await HttpClient.post('/leedflow/signup/verify-jwt', {
        token: jwtToken,
      }, "");

      setFormData(response.data.formData);
      setAccountCreated({
        userUid: response.data.accountCreated.userUid,
        company: response.data.accountCreated.company,
      });
      setCurrentStep(StepIndex.ACTIVATION_CODE);
    } catch (error) {
      console.error("Error verifying signup JWT:", error);
      message.error("Lien invalide ou expiré")
    }
  }

  useEffect(() => {
    getPlans();

    // If jwt query params submited
    const jwtToken = new URLSearchParams(window.location.search).get('token');
    if (jwtToken) {
      verifySignupJWT(jwtToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SignupFormContext.Provider
      value={{
        data: {
          formData,
          steps,
          submitError,
          currentStep,
          canProceed,
          submitButtonTarget,
          accountCreated,
          paymentSuccessful,
          isValidActivationCode,
          signupJWT,
          isLoading,
        },
        actions: {
          setCurrentStep,
          setSubmitError,
          updateFormData,
          handleCreateCompanyAndUser,
          handleConfirmAccount,
          handleSubmitAccountCreation,
        },
      }}
    >
      {children}
    </SignupFormContext.Provider>
  );
};

export default SignupFormContext;
